import React, { useEffect } from 'react';
import { LunarLandmark } from '../../types';
import { MoonImage3D } from '../MoonImage/MoonImage3D';
import './MoonDetailModal.css?v=2';

interface MoonDetailModalProps {
  landmark: LunarLandmark;
  onClose: () => void;
}

export const MoonDetailModal: React.FC<MoonDetailModalProps> = ({ 
  landmark, 
  onClose 
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  
  // Format coordinates
  const latDirection = landmark.latitude >= 0 ? 'N' : 'S';
  const lonDirection = landmark.longitude >= 0 ? 'E' : 'W';
  const latValue = Math.abs(landmark.latitude).toFixed(1);
  const lonValue = Math.abs(landmark.longitude).toFixed(1);
  
  // Format type for display
  const typeDisplay = landmark.type.charAt(0).toUpperCase() + landmark.type.slice(1);
  
  // Format difficulty with emoji
  const difficultyEmoji = {
    beginner: '⭐',
    intermediate: '⭐⭐',
    advanced: '⭐⭐⭐',
    expert: '⭐⭐⭐⭐'
  };
  
  return (
    <div 
      className="moon-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="moon-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="moon-modal-close"
          onClick={onClose}
          aria-label="Close"
          title="Close (Esc)"
        >
          ✕
        </button>
        
        <div className="moon-modal-body">
          <MoonImage3D landmark={landmark} size="large" />
          
          <div className="moon-modal-info">
            <h2 id="modal-title">{landmark.displayName}</h2>
            
            <div className="moon-modal-details">
              <div className="detail-row">
                <span className="detail-label">Coordinates:</span>
                <span className="detail-value">
                  {latValue}°{latDirection}, {lonValue}°{lonDirection}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span className="detail-value">{typeDisplay}</span>
              </div>
              
              <div className="detail-row">
                <span className="detail-label">Difficulty:</span>
                <span className="detail-value">
                  {difficultyEmoji[landmark.difficulty]} {landmark.difficulty.charAt(0).toUpperCase() + landmark.difficulty.slice(1)}
                </span>
              </div>
              
              <p className="moon-modal-description">{landmark.description}</p>
            </div>
          </div>
        </div>
        
        <button className="moon-modal-close-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

